/*
 * Purpose: Mission task receipts store — append-only per-Mission live audit
 * Last modified: 2026-06-28 | Agent
 * Completeness: 95
 */
import { create } from 'zustand';
import type { MissionTaskReceipt } from '@/services/mission-receipts/types';

const STORAGE_PREFIX = 'autoblocks:mission-task-receipts:';

type ReceiptBlob = {
  receipts: MissionTaskReceipt[];
  nextSeq: number;
};

function loadBlob(missionSessionId: string): ReceiptBlob {
  if (typeof localStorage === 'undefined' || !missionSessionId) {
    return { receipts: [], nextSeq: 1 };
  }
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${missionSessionId}`);
    if (!raw) return { receipts: [], nextSeq: 1 };
    const parsed = JSON.parse(raw) as ReceiptBlob;
    const receipts = Array.isArray(parsed.receipts) ? parsed.receipts : [];
    const maxSeq = receipts.reduce((m, r) => Math.max(m, r.seq ?? 0), 0);
    return { receipts, nextSeq: parsed.nextSeq ?? maxSeq + 1 };
  } catch {
    return { receipts: [], nextSeq: 1 };
  }
}

function persistBlob(missionSessionId: string, blob: ReceiptBlob) {
  if (typeof localStorage === 'undefined' || !missionSessionId) return;
  localStorage.setItem(`${STORAGE_PREFIX}${missionSessionId}`, JSON.stringify(blob));
}

interface MissionTaskReceiptStore {
  missionSessionId: string;
  receipts: MissionTaskReceipt[];
  nextSeq: number;
  setMissionSession: (id: string) => void;
  append: (input: Omit<MissionTaskReceipt, 'seq' | 'id' | 'at'> & { at?: string }) => MissionTaskReceipt | null;
}

export const useMissionTaskReceiptStore = create<MissionTaskReceiptStore>((set, get) => ({
  missionSessionId: '',
  receipts: [],
  nextSeq: 1,

  setMissionSession: (id) => {
    const blob = loadBlob(id);
    set({ missionSessionId: id, receipts: blob.receipts, nextSeq: blob.nextSeq });
  },

  append: (input) => {
    const { missionSessionId, receipts, nextSeq } = get();
    if (!missionSessionId || !input.summary.trim()) return null;

    const receipt: MissionTaskReceipt = {
      seq: nextSeq,
      id: `mtr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      at: input.at ?? new Date().toISOString(),
      missionSessionId,
      dossierEntryId: input.dossierEntryId,
      actor: input.actor,
      kind: input.kind,
      title: input.title.trim(),
      summary: input.summary.trim(),
      mapDelta: input.mapDelta,
    };

    const nextReceipts = [...receipts, receipt];
    persistBlob(missionSessionId, { receipts: nextReceipts, nextSeq: nextSeq + 1 });
    set({ receipts: nextReceipts, nextSeq: nextSeq + 1 });
    return receipt;
  },
}));

export function bindMissionTaskReceiptSession(missionSessionId: string): void {
  useMissionTaskReceiptStore.getState().setMissionSession(missionSessionId);
}
